import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';

import { Observable, race, throwError } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';

import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { environment } from '../../../environments/environment';
import { Student } from '../../shared/models/entities/student.entity';
import { LocaleKeys } from '../../shared/utils/locale-keys';
import { EnvironmentService } from '../services/environment.service';
import { HttpAuthService } from '../services/http/http-auth.service';
import * as AuthActions from '../state/auth-state/auth.actions';
import * as AuthSelectors from '../state/auth-state/auth.selectors';

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const actions$ = inject(Actions);
  const router = inject(Router);
  const store = inject(Store);
  const environmentService = inject(EnvironmentService);
  const httpAuthService = inject(HttpAuthService);

  const addToken = (request: HttpRequest<any>, token: string) => {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const impersonateUser = (request: HttpRequest<any>, student: Student) => {
    let params = request.params;
    params = params.set('_switch_user', student.username);
    return request.clone({
      params: params,
    });
  };

  const addAppVersionAndSourceUrl = (request: HttpRequest<any>) => {
    let headers = request.headers.append(
      'X-App-Version',
      environmentService.stringifiedFrontAppCompleteVersion
    );
    headers = headers.append('X-App-Source-Url', router.url);
    return request.clone({ headers });
  };

  const byPassRequestFor401ErrorHandling = (request: HttpRequest<any>) => {
    return request.url.includes(httpAuthService.refreshAuthTokenEndpoint);
  };

  // nnkitodo [v2later auth] voir si on peut utiliser un exhaustmap pour simplifier le process (cf global entities)
  const handle401Error = (
    request: HttpRequest<any>,
    next: HttpHandlerFn,
    httpError: HttpErrorResponse
  ): Observable<HttpEvent<any>> => {
    return store.select(AuthSelectors.selectAuth).pipe(
      take(1),
      tap((authState) => {
        if (!authState.refreshingToken && localStorage.getItem(LocaleKeys.refreshToken)) {
          store.dispatch(
            AuthActions.refreshToken({
              refreshToken: localStorage.getItem(LocaleKeys.refreshToken),
            })
          );
        }
      }),
      switchMap((authState) => {
        if (localStorage.getItem(LocaleKeys.refreshToken)) {
          return race(
            actions$.pipe(
              ofType(AuthActions.refreshTokenSucceed),
              take(1),
              switchMap((action) => next(addToken(request, action.result.token)))
            ),
            actions$.pipe(
              ofType(AuthActions.refreshTokenFailed),
              take(1),
              switchMap(() => throwError(() => httpError))
            )
          );
        } else {
          return throwError(() => httpError);
        }
      })
    );
  };

  const handle503Error = (
    request: HttpRequest<any>,
    next: HttpHandlerFn,
    httpError: HttpErrorResponse
  ): Observable<HttpEvent<any>> => {
    return store.select(AuthSelectors.selectAuth).pipe(
      take(1),
      tap((authState) => {
        if (!authState.isInMaintenance) {
          store.dispatch(AuthActions.triggerMaintenance({ message: httpError.error?.message }));
        }
      }),
      switchMap(() => {
        return throwError(() => httpError);
      })
    );
  };

  return store.select(AuthSelectors.impersonateUser).pipe(
    take(1),
    switchMap((impersonateUserValue) => {
      const accessToken = localStorage.getItem(LocaleKeys.accessToken);
      if (!request.url.includes('https://cdn.')) {
        if (accessToken) {
          request = addToken(request, accessToken);
        }
        if (impersonateUserValue) {
          request = impersonateUser(request, impersonateUserValue);
        }
        if (environment.env === 'dev' || environment.env === 'staging') {
          request = addAppVersionAndSourceUrl(request);
        }
      }
      return next(request);
    }),
    // nnkitodo [v2later] mieux gérer erreur par défaut
    // nnkitodo [v2later] essayer de catcher les 500 : erreur sur setaspaid writer bill sur folder déjà payé, on n'a pas d'erreur qui pop
    catchError((error: HttpEvent<any>) => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 401 && !byPassRequestFor401ErrorHandling(request)) {
          return handle401Error(request, next, error);
        } else if (error.status === 503) {
          return handle503Error(request, next, error);
        } else {
          console.error(`[HTTP ERROR ${error.status}]`, error);
        }
      } else {
        console.error('[UNDEFINED REQUEST ERROR]', error);
      }
      return throwError(() => error);
    })
  );
};
